import React from 'react';

interface ErrorComponentProps {
  errorText?: string;
  heading?: string;
  subheading?: string;
  bottomText?: string;
  onRetry?: () => void;
}

const ErrorComponent: React.FC<ErrorComponentProps> = ({
  errorText = "An error occurred",
  heading = "We Encountered a Problem",
  subheading = "Something went wrong while loading your test environment",
  bottomText = "Please try again or contact support if the issue persists",
  onRetry
}) => {
  return (
    <div className="bg-gray-50 flex flex-col items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        {/* Animated SVG illustration for error state */}
        <div className="relative h-48 mb-8">
          <svg className="w-full h-full" viewBox="0 0 240 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Warning/Error sign */}
            <circle cx="125" cy="50" r="30" fill="#FEE2E2" stroke="#EF4444" strokeWidth="2" />
            <path d="M125 30V60" stroke="#EF4444" strokeWidth="4" strokeLinecap="round" />
            <circle cx="125" cy="70" r="2" fill="#EF4444" />
            
            {/* Scattered papers/books to indicate something went wrong */}
            <g className="animate-pulse" style={{ animationDuration: '4s' }}>
              <rect x="35" y="70" width="40" height="10" rx="2" transform="rotate(-15 35 70)" fill="#FECACA" />
              <rect x="40" y="50" width="30" height="10" rx="2" transform="rotate(10 40 50)" fill="#FCA5A5" />
              <rect x="55" y="80" width="20" height="10" rx="2" transform="rotate(-5 55 80)" fill="#F87171" />
            </g>
            
            {/* Test paper with an X */}
            <g>
              <rect x="175" y="30" width="40" height="55" rx="3" fill="#FEE2E2" stroke="#EF4444" strokeWidth="1" />
              
              {/* X mark */}
              <path d="M190 45L200 55M200 45L190 55" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" />
              
              {/* Question content lines */}
              <rect x="185" y="65" width="20" height="2" rx="1" fill="#FCA5A5" />
              <rect x="185" y="70" width="25" height="2" rx="1" fill="#FCA5A5" />
              <rect x="185" y="75" width="22" height="2" rx="1" fill="#FCA5A5" />
            </g>
            
            {/* Broken clock face */}
            <g>
              <circle cx="75" cy="50" r="25" fill="#FEE2E2" stroke="#EF4444" strokeWidth="2" />
              {/* <circle cx="75" cy="50" r="3" fill="#EF4444" /> */}
              
              {/* Broken clock hands */}
              {/* <line 
                x1="75" y1="50" x2="85" y2="30" 
                stroke="#EF4444" 
                strokeWidth="2" 
                strokeLinecap="round"
                className="animate-ping"
                style={{ animationDuration: '2s', transformOrigin: '75px 50px', opacity: 0.7 }}
              />
              
              <line 
                x1="75" y1="50" x2="95" y2="60" 
                stroke="#EF4444" 
                strokeWidth="1.5" 
                strokeLinecap="round"
                className="animate-ping"
                style={{ animationDuration: '1.5s', transformOrigin: '75px 50px', opacity: 0.7 }}
              /> */}
              
              {/* Crack in clock */}
              <path 
                d="M65 30 L80 50 L60 65" 
                stroke="#EF4444" 
                strokeWidth="1.5" 
                strokeLinecap="round"
                fill="none"
              />
            </g>
            
            {/* Floating elements - error related */}
            <path d="M30 20 L38 28 M38 20 L30 28" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" className="animate-pulse" style={{ animationDuration: '3s' }} />
            <path d="M185 15 L193 23 M193 15 L185 23" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" className="animate-pulse" style={{ animationDuration: '2.5s' }} />
            <path d="M165 85 L173 93 M173 85 L165 93" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" className="animate-pulse" style={{ animationDuration: '3.5s' }} />
          </svg>
          
          {/* Error indicator */}
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-70 backdrop-blur-sm px-4 py-2 rounded-full flex items-center">
            <div className="mr-3 h-5 w-5 text-red-600">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-red-800 font-medium">{errorText}</span>
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-gray-800 mb-3">{heading}</h2>
        <p className="text-gray-600 mb-6">{subheading}</p>
        
        {onRetry && (
          <button 
            onClick={onRetry}
            className="mb-6 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Try Again
          </button>
        )}
        
        <span className="text-sm text-gray-500">{bottomText}</span>
      </div>
    </div>
  );
};

export default ErrorComponent;
